import { useState } from 'react';
import type { EntryTimingIntradayBucket, EntryTimingProfile } from '@einhaus/project314-types';
import { Callout, Card, FormSelect } from '@/components/UI';
import {
    buildEntryTimingCellTitle,
    formatEntryTimingBucketLabel,
    formatEntryTimingMetricValue,
    formatEntryTimingSemantics,
    getAvailableEntryTimingMetrics,
    getEntryTimingCell,
    getEntryTimingCellEvidence,
    getEntryTimingCellStyle,
    getEntryTimingMetricScale,
    getEntryTimingMetricValue,
    getEntryTimingWeekdays,
    selectDefaultEntryTimingMetric,
    type EntryTimingMetricKey
} from './entryTimingProfilePresentation';

export interface EntryTimingProfilePanelProps {
    profile: EntryTimingProfile;
    title?: string;
    metric?: EntryTimingMetricKey;
    onMetricChange?: (metric: EntryTimingMetricKey) => void;
    onCellSelect?: (cell: EntryTimingProfile['cells'][number]) => void;
    selectedCell?: Pick<EntryTimingProfile['cells'][number], 'weekday' | 'intradayBucketKey'> | null;
}

function formatOptionalPercent(value: number | null): string {
    return value === null ? '—' : `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
}

function populationLabel(profile: EntryTimingProfile): string {
    return profile.population === 'signalCandidate' ? 'Signal candidates' : 'Portfolio accepted';
}

function windowLabel(profile: EntryTimingProfile): string {
    switch (profile.windowRole) {
        case 'training':
            return 'Training';
        case 'walkForward':
            return 'Walk-forward';
        case 'wholeRange':
            return 'Whole range';
    }
}

function SummaryStat({ label, value }: { label: string; value: string }) {
    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-950/40 px-3 py-2">
            <div className="text-xs uppercase tracking-wide text-zinc-500">{label}</div>
            <div className="mt-1 text-sm font-medium tabular-nums text-zinc-200">{value}</div>
        </div>
    );
}

function TimingCell({
    profile,
    bucket,
    weekday,
    metric,
    scale,
    onSelect,
    selected
}: {
    profile: EntryTimingProfile;
    bucket: EntryTimingIntradayBucket | null;
    weekday: EntryTimingProfile['cells'][number]['weekday'];
    metric: EntryTimingMetricKey;
    scale: number;
    onSelect?: (cell: EntryTimingProfile['cells'][number]) => void;
    selected: boolean;
}) {
    const cell = getEntryTimingCell(profile, weekday, bucket?.key ?? null);

    if (!cell) {
        return (
            <td
                aria-label={`No entries for ${weekday}`}
                className="min-w-24 border border-zinc-800 bg-zinc-950/20 px-2 py-3 text-center text-zinc-700"
            >
                —
            </td>
        );
    }

    const value = getEntryTimingMetricValue(cell.metrics, metric);
    const style = getEntryTimingCellStyle(profile, cell, metric, scale);
    const evidence = getEntryTimingCellEvidence(profile, cell);
    const title = buildEntryTimingCellTitle(profile, cell, metric, bucket);
    const content = (
        <>
            <div className="text-sm font-medium text-zinc-100">{formatEntryTimingMetricValue(value, metric)}</div>
            <div className="mt-0.5 text-[11px] text-zinc-400">n={cell.metrics.entryCount.toLocaleString('en-US')}</div>
            {evidence.thin && (
                <div className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-300">
                    Low support<span className="sr-only">: {evidence.reasons.join('; ')}</span>
                </div>
            )}
        </>
    );

    return (
        <td className="min-w-24 border border-zinc-800 p-0 text-center tabular-nums transition-opacity" style={style} title={title}>
            {onSelect ? (
                <button
                    aria-label={`${title}. Select this timing cell`}
                    aria-pressed={selected}
                    className={`w-full px-2 py-2 transition hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-inset ${selected ? 'ring-2 ring-sky-400 ring-inset' : ''}`}
                    type="button"
                    onClick={() => onSelect(cell)}
                >
                    {content}
                </button>
            ) : (
                <div className="px-2 py-2">{content}</div>
            )}
        </td>
    );
}

/** Shared presentation module for experiment, Idea Lab, and portfolio entry-timing reports. */
export function EntryTimingProfilePanel({
    profile,
    title = 'Entry Timing Profile',
    metric: controlledMetric,
    onMetricChange,
    onCellSelect,
    selectedCell = null
}: EntryTimingProfilePanelProps) {
    const availableMetrics = getAvailableEntryTimingMetrics(profile);
    const defaultMetric = selectDefaultEntryTimingMetric(profile);
    const [internalMetric, setInternalMetric] = useState<EntryTimingMetricKey>(defaultMetric);
    const requestedMetric = controlledMetric ?? internalMetric;
    const metric = availableMetrics.some((candidate) => candidate.key === requestedMetric) ? requestedMetric : defaultMetric;
    const scale = getEntryTimingMetricScale(profile, metric);
    const weekdays = getEntryTimingWeekdays(profile);
    const isDaily = profile.semantics.intradayBinning.mode === 'none';
    const buckets: (EntryTimingIntradayBucket | null)[] = isDaily ? [null] : profile.intradayBuckets;
    const excludedCount = profile.coverage.sourceEntryCount - profile.coverage.includedEntryCount;

    const selectMetric = (nextMetric: EntryTimingMetricKey) => {
        setInternalMetric(nextMetric);
        onMetricChange?.(nextMetric);
    };

    const selectMetricFromValue = (value: string) => {
        const nextMetric = availableMetrics.find((candidate) => candidate.key === value);

        if (nextMetric) selectMetric(nextMetric.key);
    };

    return (
        <Card
            bodyClassName="space-y-4"
            headerActions={
                <FormSelect
                    aria-label="Entry timing heatmap metric"
                    className="min-w-44"
                    fullWidth={false}
                    noMargin
                    onChange={(event) => selectMetricFromValue(event.target.value)}
                    options={availableMetrics.map((candidate) => ({ label: candidate.label, value: candidate.key }))}
                    size="sm"
                    value={metric}
                />
            }
            subtitle={`${populationLabel(profile)} · ${windowLabel(profile)}`}
            title={title}
        >
            <div className="text-sm text-zinc-400">{formatEntryTimingSemantics(profile)}</div>

            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
                <SummaryStat label="Entries" value={profile.summary.entryCount.toLocaleString('en-US')} />
                <SummaryStat
                    label="Dates / entities"
                    value={`${profile.summary.distinctDateCount} / ${profile.summary.distinctEntityCount}`}
                />
                <SummaryStat label="Mean return" value={formatOptionalPercent(profile.summary.meanReturnPercent)} />
                <SummaryStat label="Median return" value={formatOptionalPercent(profile.summary.medianReturnPercent)} />
                <SummaryStat
                    label="Timing edge"
                    value={
                        profile.summary.relativeEdge === null
                            ? '—'
                            : `${formatOptionalPercent(profile.summary.relativeEdge.meanPercent)} (${profile.summary.relativeEdge.coveredCount}/${profile.summary.relativeEdge.eligibleCount})`
                    }
                />
            </div>

            {excludedCount > 0 && (
                <div className="text-xs text-zinc-500">
                    Coverage: {profile.coverage.includedEntryCount.toLocaleString('en-US')} of{' '}
                    {profile.coverage.sourceEntryCount.toLocaleString('en-US')} source entries assigned to timing cells.
                </div>
            )}

            {profile.warnings.map((warning, index) => (
                <Callout key={`${warning.code}-${index}`} title="Timing-profile caveat" variant="warning">
                    {warning.message}
                </Callout>
            ))}

            {weekdays.length === 0 ? (
                <div className="rounded-md border border-dashed border-zinc-800 px-4 py-8 text-center text-sm text-zinc-500">
                    No entries were available for this timing profile.
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse text-sm">
                        <caption className="sr-only">
                            {title} by weekday and {isDaily ? 'weekday total' : 'intraday bucket'}
                        </caption>
                        <thead>
                            <tr>
                                <th
                                    className="sticky left-0 z-10 border border-zinc-800 bg-zinc-950 px-3 py-2 text-left font-medium text-zinc-400"
                                    scope="col"
                                >
                                    Weekday
                                </th>
                                {buckets.map((bucket) => (
                                    <th
                                        className="border border-zinc-800 bg-zinc-950 px-2 py-2 text-center text-xs font-medium text-zinc-400"
                                        key={bucket?.key ?? 'weekday-total'}
                                        scope="col"
                                    >
                                        {bucket === null || profile.semantics.intradayBinning.mode === 'none'
                                            ? 'Weekday total'
                                            : formatEntryTimingBucketLabel(bucket, profile.semantics.intradayBinning.mode)}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {weekdays.map((weekday) => (
                                <tr key={weekday}>
                                    <th
                                        className="sticky left-0 z-10 border border-zinc-800 bg-zinc-950 px-3 py-2 text-left font-medium capitalize text-zinc-300"
                                        scope="row"
                                    >
                                        {weekday}
                                    </th>
                                    {buckets.map((bucket) => (
                                        <TimingCell
                                            bucket={bucket}
                                            key={`${weekday}-${bucket?.key ?? 'weekday-total'}`}
                                            metric={metric}
                                            onSelect={onCellSelect}
                                            profile={profile}
                                            scale={scale}
                                            selected={
                                                selectedCell?.weekday === weekday &&
                                                selectedCell.intradayBucketKey === (bucket?.key ?? null)
                                            }
                                            weekday={weekday}
                                        />
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <p className="text-xs text-zinc-500">
                {onCellSelect
                    ? 'Select a cell to create an editable child variant. The new rule still requires a fresh backtest; this view does not estimate that counterfactual result.'
                    : 'This is descriptive evidence. Creating a timing rule requires a child variant and a fresh backtest; this view does not estimate that counterfactual result.'}
            </p>
        </Card>
    );
}
